import type React from 'react';
import moment from 'moment';
import { webFormatCurrency } from './helper';

// ============================================================================
// Types & Interfaces
// ============================================================================

export interface EntertainmentCreditFormData {
  eligible_service_dropdown: string;
  service_purchase_method_dropdown: string;
  account_email: string;
  rogers_account_number: string;
  rogers_transaction_date: string;
  rogers_transaction_date_transaction_info: string;
  rogers_amount_billed: string;
  rogers_claim_amount: string;
  transaction_date: string;
  amount_billed: string;
  claim_amount: string;
  [key: string]: string | boolean;
}

export interface EntertainmentCreditFormErrors {
  [key: string]: string;
}

export interface EntertainmentCreditFieldConfig {
  id: string;
  type: string;
  title?: string;
  infoTip?: string;
  placeholder?: string;
  infoPlaceHolder?: string;
  requiredField?: boolean;
  dateFormat?: string;
  validation?: any;
  errorMessages?: any;
  options?: Array<{ label: string; value: string }>;
  hint?: string;
  placeholderAppendRequiredSymbol?: boolean;
  isErrorMessageRequired?: boolean;
}

// ============================================================================
// Field Utilities
// ============================================================================

export const clearRogersFields = (
  setFormData: React.Dispatch<React.SetStateAction<EntertainmentCreditFormData>>,
  setErrors: React.Dispatch<React.SetStateAction<EntertainmentCreditFormErrors>>
) => {
  const rogersFields = [
    'account_email',
    'rogers_account_number',
    'rogers_transaction_date',
    'rogers_transaction_date_transaction_info',
    'rogers_amount_billed',
    'rogers_claim_amount',
  ];

  setFormData((prev) => ({
    ...prev,
    ...Object.fromEntries(rogersFields.map((field) => [field, ''])),
  }));

  setErrors((prev) => {
    const newErrors = { ...prev };
    rogersFields.forEach((field) => delete newErrors[field]);
    return newErrors;
  });
};

export const getRequiredFields = (showRogersAccountSection: boolean): string[] => {
  const baseFields = [
    'eligible_service_dropdown',
    'service_purchase_method_dropdown',
  ];
  const rogersFields = [
    'account_email',
    'rogers_account_number',
    'rogers_transaction_date',
    'rogers_transaction_date_transaction_info',
    'rogers_amount_billed',
    'rogers_claim_amount',
  ];
  const nonRogersFields = ['transaction_date', 'amount_billed', 'claim_amount'];

  return showRogersAccountSection
    ? [...baseFields, ...rogersFields]
    : [...baseFields, ...nonRogersFields];
};

// ============================================================================
// Date Utilities
// ============================================================================

export const getDateInputType = (format: string): 'date' | 'month' => {
  return format.includes('MM') &&
    format.includes('Y') &&
    !format.includes('DD') &&
    !format.includes('D')
    ? 'month'
    : 'date';
};

// ============================================================================
// Currency Formatting
// ============================================================================

export const parseCurrencyValue = (formattedValue: string): string => {
  if (!formattedValue) return '';
  return formattedValue.replace(/[^\d.,]/g, '').replace(/,/g, '.');
};

export const formatCurrencyInput = (value: string, isFrench: boolean): string => {
  if (!value) return '';
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return '';
  return webFormatCurrency(numValue, isFrench ? 'fr' : 'en');
};

// ============================================================================
// Validation Functions
// ============================================================================

export const validateEmail = (
  value: string,
  fieldConfig: EntertainmentCreditFieldConfig
): string => {
  const emailRegex = /^[^\s@]{1,64}@[^\s@]{1,255}\.[^\s@]{1,63}$/;
  return !emailRegex.test(value)
    ? fieldConfig?.errorMessages?.errorMessageIncorrectEmail ||
        'Invalid email address'
    : '';
};

export const validateNumber = (
  value: string,
  fieldConfig: EntertainmentCreditFieldConfig
): string => {
  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return fieldConfig?.errorMessages?.required || 'Please enter a valid number';
  }

  const { validation, errorMessages } = fieldConfig;
  if (validation?.min !== undefined && numValue < validation.min) {
    return errorMessages?.min || `Minimum value is ${validation.min}`;
  }
  if (
    validation?.maxCheckEnabled &&
    validation?.max !== undefined &&
    numValue > validation.max
  ) {
    return errorMessages?.max || `Maximum value is ${validation.max}`;
  }

  return '';
};

/**
 * Converts French/Contentful date format to Moment.js format
 * JJ (jour) -> DD (day)
 * MM (mois) -> MM (month) 
 * AAAA (année) -> YYYY (year)
 */
const convertToMomentFormat = (contentfulFormat: string): string => {
  if (!contentfulFormat) return 'MM/DD/YYYY';
  
  return contentfulFormat
    .replace(/JJ/g, 'DD')      // French day format
    .replace(/AAAA/g, 'YYYY')  // French year format (4 digits)
    .replace(/AA/g, 'YY');      // French year format (2 digits, if used)
  // MM stays the same as it's used in both French and English
};

export const validateDate = (
  value: string,
  fieldConfig: EntertainmentCreditFieldConfig
): string => {
  const contentfulFormat = fieldConfig?.dateFormat || 'MM/DD/YYYY';
  const momentFormat = convertToMomentFormat(contentfulFormat);

  const date = moment(value, momentFormat, true);

  if (!date.isValid()) {
    return `Please enter a valid date in ${contentfulFormat} format`;
  }

  const { validation, errorMessages } = fieldConfig;

  if (validation?.maxDate === 'today') {
    const today = moment().endOf('day');
    if (date.isAfter(today)) {
      return errorMessages?.maxDate || 'Date cannot be in the future';
    }
  }

  if (validation?.minDate) {
    const minDate = moment(validation.minDate, momentFormat, true);
    if (minDate.isValid() && date.isBefore(minDate)) {
      return errorMessages?.minDate || 'Date not allowed';
    }
  }

  return '';
};

export const validateString = (
  value: string,
  fieldConfig: EntertainmentCreditFieldConfig
): string => {
  const { validation, errorMessages } = fieldConfig;

  if (!value && fieldConfig.requiredField) {
    return (
      errorMessages?.errorMessageEmpty ||
      errorMessages?.required ||
      'This field is required'
    );
  }

  if (value) {
    if (
      validation?.minLength !== undefined &&
      value.length < validation.minLength
    ) {
      return (
        errorMessages?.minCharsError ||
        `Minimum ${validation.minLength} characters required`
      );
    }

    if (
      validation?.maxLengthEnabled &&
      validation?.maxLenght !== undefined &&
      value.length > validation.maxLenght
    ) {
      return (
        errorMessages?.maxCharsError ||
        `Maximum ${validation.maxLenght} characters allowed`
      );
    }
  }

  return '';
};

export const validateField = (
  fieldId: string,
  value: string | boolean,
  fieldConfig: EntertainmentCreditFieldConfig,
  formData: EntertainmentCreditFormData
): string => {
  if (!fieldConfig?.requiredField && !value) return '';

  if (fieldConfig?.requiredField && !value) {
    if (fieldConfig?.isErrorMessageRequired === false) {
      return '';
    }
    return (
      fieldConfig?.errorMessages?.required ||
      fieldConfig?.errorMessages?.errorMessageEmpty ||
      'This field is required'
    );
  }

  const valueStr = value as string;
  const validators: Record<
    string,
    (val: string, config: EntertainmentCreditFieldConfig) => string
  > = {
    email: validateEmail,
    number: validateNumber,
    date: validateDate,
    string: validateString,
  };

  const validator = validators[fieldConfig.type];
  const baseError = validator ? validator(valueStr, fieldConfig) : '';
  if (baseError) return baseError;

  // Cross-field: claim amount must not exceed billed amount
  if (fieldId === 'claim_amount' || fieldId === 'rogers_claim_amount') {
    const billedFieldId =
      fieldId === 'claim_amount' ? 'amount_billed' : 'rogers_amount_billed';
    const billedValue = parseFloat(String(formData[billedFieldId]));
    const claimValue = parseFloat(valueStr);
    if (!isNaN(billedValue) && !isNaN(claimValue) && claimValue > billedValue) {
      return (
        fieldConfig?.errorMessages?.greaterThanBilledAmount ||
        'Claim amount cannot exceed the billed amount.'
      );
    }
  }

  return '';
};

// ============================================================================
// Payload Builder
// ============================================================================

export const buildEntertainmentCreditPayload = (
  formData: EntertainmentCreditFormData,
  recaptchaToken?: string,
  isFrench?: boolean
): Record<string, string> => {
  // Helper to parse date from various formats
  const parseDate = (dateStr: string, expectedFormat: string) => {
    if (!dateStr) return moment();
    
    // Try parsing with the expected format first
    let parsed = moment(dateStr, expectedFormat, true);
    if (parsed.isValid()) return parsed;
    
    // Fallback: try format based on language preference
    const primaryFormats = isFrench 
      ? ['DD/MM/YYYY', 'MM/YYYY', 'MM/DD/YYYY'] 
      : ['MM/DD/YYYY', 'DD/MM/YYYY', 'MM/YYYY'];
    
    for (const fallback of primaryFormats) {
      parsed = moment(dateStr, fallback, true);
      if (parsed.isValid()) return parsed;
    }
    
    // Last resort: ISO format
    parsed = moment(dateStr, 'YYYY-MM-DD', true);
    if (parsed.isValid()) return parsed;
    
    return moment(dateStr);
  };

  if (formData.service_purchase_method_dropdown === 'rogers_account') {
    return {
      eligibleService: String(formData.eligible_service_dropdown).split(':')[0],
      purchaseDate: String(
        parseDate(
          formData.rogers_transaction_date_transaction_info,
          'DD/MM/YYYY'
        ).format('YYYY-MM-DD')
      ),
      amountBilled: String(formData.rogers_amount_billed),
      amountClaimed: String(formData.rogers_claim_amount),
      purchaseSource: String(
        formData.service_purchase_method_dropdown
      ).toUpperCase(),
      banAccountId: String(formData.rogers_account_number),
      billedDate: String(
        parseDate(formData.rogers_transaction_date, 'MM/YYYY').format('MM-YYYY')
      ),
      emailAddress: String(formData.account_email),
      ...(recaptchaToken !== undefined ? { recaptchaToken } : {}),
    };
  }

  return {
    eligibleService: String(formData.eligible_service_dropdown).split(':')[0],
    purchaseDate: String(
      parseDate(formData.transaction_date, 'DD/MM/YYYY').format('YYYY-MM-DD')
    ),
    amountBilled: String(formData.amount_billed),
    amountClaimed: String(formData.claim_amount),
    purchaseSource: String(
      formData.service_purchase_method_dropdown
    ).toUpperCase(),
    ...(recaptchaToken !== undefined ? { recaptchaToken } : {}),
  };
};
